import React, { useMemo } from "react";

import { Button, Checkbox, IconButton, Stack, TableCell, TableRow, Typography } from "@mui/material";
import { BinDeleteIcon } from "assets/material-custom-icons";
import { RecommendationField, useAiDataContext } from "components/AiData/context/AiData-context";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import dayjs from "dayjs";
import { useFormatNumericHook } from "hooks";
import { IRecommendationItem, RecommendationStatus } from "store/types";

import { currencySign } from "../../../../constants";
import { CellWithTooltip } from "../CellWithTooltip";
import { UpdateStatusDropdown } from "../UpdateStatusDropdown";

interface ParameterRowProps {
  parameter: IRecommendationItem;
  dealId: number;
  fileId?: number;
  nestedLevel: "noGroup" | "groupedByMeasure" | "groupedByMeasureAndInvestor";
}
const bodyCellStyle = {
  fontSize: "16px",
  fontWeight: "400",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  position: "relative",
  maxWidth: "140px"
};

export const ParameterRow: React.FC<ParameterRowProps> = ({ parameter, dealId, nestedLevel, fileId }) => {
  const formatNumeric = useFormatNumericHook();
  const {
    onChangeRecommendationStatus,
    isRecommendationWithChildrenFilled,
    handleCellClick,
    handleHierarchicalCheckboxChange,
    isUniqueIdSelected,
    generateUniqueId,
    userHasEditPermission,
    getMissingRequiredFields
  } = useAiDataContext();

  const isActive = useMemo(
    () => parameter.status === RecommendationStatus.new && !parameter.children?.length && userHasEditPermission,
    [parameter.status, parameter.children, userHasEditPermission]
  );

  const paddingLeft = useMemo(() => {
    if (nestedLevel === "noGroup") return "35px";
    if (nestedLevel === "groupedByMeasure") return "65px";
    if (nestedLevel === "groupedByMeasureAndInvestor") return "95px";
    return "35px";
  }, [nestedLevel]);

  const showActions = useMemo(() => {
    if (parameter.status !== RecommendationStatus.new || !userHasEditPermission) return false;
    return true;
  }, [parameter.status, parameter.children]);

  const isGlobalValueMeasure = useMemo(() => {
    return parameter.measure?.is_global_value;
  }, [parameter.measure]);

  const missingFields = useMemo(() => {
    return getMissingRequiredFields(parameter);
  }, [parameter]);

  return (
    <React.Fragment>
      <TableRow
        sx={{
          borderBottom: "1px solid #DBE8F5",
          "& td": {
            borderBottom: "1px solid #DBE8F5"
          },
          "& td:last-child > *": {
            opacity: 0,
            transition: "opacity 0.2s ease-in-out"
          },
          "&:hover td:last-child > *": {
            opacity: 1
          }
        }}
      >
        <TableCell
          sx={{
            padding: "0 16px !important",
            ...bodyCellStyle,
            maxWidth: "200px",
            paddingLeft: `${paddingLeft} !important`
          }}
        >
          <Stack direction="row" alignItems="center">
            <Checkbox
              disabled={
                !userHasEditPermission ||
                parameter.status === RecommendationStatus.accepted ||
                parameter.status === RecommendationStatus.declined
              }
              checked={fileId ? isUniqueIdSelected(generateUniqueId(parameter, fileId), parameter, fileId) : false}
              onChange={(event) =>
                fileId &&
                handleHierarchicalCheckboxChange(
                  generateUniqueId(parameter, fileId),
                  event.target.checked,
                  parameter,
                  fileId
                )
              }
              sx={{
                padding: "4px",
                "&:hover": {
                  backgroundColor: "transparent"
                },
                "& .MuiCheckbox-root:hover": {
                  backgroundColor: "transparent"
                }
              }}
            />
            <MaterialTooltip title={parameter.measure?.name || ""}>
              <Typography
                noWrap
                onClick={(event) =>
                  isActive &&
                  handleCellClick({ event, data: { parameter, dealId }, field: RecommendationField.measure })
                }
                style={{
                  color: isActive ? "#26A6AE" : "inherit",
                  cursor: isActive ? "pointer" : "default",
                  display: "block",
                  width: "100%"
                }}
              >
                {parameter?.measure?.name || "-"}
              </Typography>
            </MaterialTooltip>
          </Stack>
        </TableCell>

        <CellWithTooltip
          isActive={isActive && !isGlobalValueMeasure}
          value={parameter.investor?.name || "-"}
          style={{ ...bodyCellStyle, maxWidth: "150px" }}
          onClick={(event) =>
            handleCellClick({
              event,
              data: { parameter, dealId, is_global_value: parameter.measure?.is_global_value },
              field: RecommendationField.investor
            })
          }
        />
        <CellWithTooltip
          currency={currencySign[parameter.currency || ""]}
          isActive={isActive}
          value={
            parameter.children?.length
              ? formatNumeric({ value: parameter.value, format: { abbreviated: true, decimal: 2 } })
              : parameter.value
          }
          style={bodyCellStyle}
          onClick={(event) => handleCellClick({ event, data: { parameter, dealId }, field: RecommendationField.value })}
        />
        <CellWithTooltip
          isActive={isActive}
          value={parameter.date ? dayjs(parameter.date).format("DD/MM/YYYY") : ""}
          style={bodyCellStyle}
          onClick={(event) => handleCellClick({ event, data: { parameter, dealId }, field: RecommendationField.date })}
        />
        <CellWithTooltip value={parameter.reference || ""} style={{ ...bodyCellStyle, maxWidth: "150px" }} />
        <CellWithTooltip
          isActive={isActive}
          value={parameter.classification || ""}
          style={bodyCellStyle}
          onClick={(event) =>
            handleCellClick({ event, data: { parameter, dealId }, field: RecommendationField.classification })
          }
        />

        <TableCell>
          {parameter.status && (
            <UpdateStatusDropdown status={parameter.status} parameter={parameter as IRecommendationItem} />
          )}
        </TableCell>

        <TableCell sx={{ width: "100px" }}>
          <Stack direction="row" alignItems="center" gap={1}>
            {parameter.status === RecommendationStatus.new && userHasEditPermission && (
              <IconButton
                onClick={() => {
                  parameter.id && onChangeRecommendationStatus(RecommendationStatus.declined, parameter.id);
                }}
              >
                <BinDeleteIcon />
              </IconButton>
            )}
            {showActions && (
              <MaterialTooltip
                title={
                  !isRecommendationWithChildrenFilled(parameter)
                    ? `Missing required fields (${missingFields.join(", ")})`
                    : ""
                }
                isShow
              >
                <span>
                  <Button
                    onClick={() =>
                      parameter.id && onChangeRecommendationStatus(RecommendationStatus.accepted, parameter.id)
                    }
                    variant="contained"
                    color="primary"
                    disabled={!isRecommendationWithChildrenFilled(parameter)}
                  >
                    Approve
                  </Button>
                </span>
              </MaterialTooltip>
            )}
          </Stack>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};
